<template>
    <div
        v-qa="'non-sku-stone-search'"
        class="stone-search"
    >
        <div
            v-qa="'before-filters-content'"
            class="before-filters-content"
        >
            <slot name="before-filters"></slot>
        </div>

        <GridContentLoader v-if="!bounds && fetchingOrStale" />

        <div v-else-if="bounds">
            <div class="quick-filters">
                <div class="filters">
                    <div class="padded">
                        <slot name="filters-quick-padded"></slot>
                    </div>

                    <slot name="filters-quick"></slot>
                </div>
            </div>

            <div class="mobile-stone-tabs">
                <StoneSearchTabs
                    :shape="gemsApiShapeToShapeCode(shape)"
                    class="tabs"
                />
            </div>

            <GridContentLoader v-if="fetchingOrStale && !data" />

            <StoneSearchFiveHundredError
                v-else-if="error"
                :errors
                :route-reset
                @retry="retry"
            />

            <div
                v-else-if="data"
                ref="gridElement"
                v-qa="'stone-grid'"
                v-qa.fetching="fetchingOrStale"
                class="stone-grid-wrapper"
            >
                <div class="grid">
                    <div
                        class="grid-sidebar"
                        data-qa="left-sidebar"
                    >
                        <div
                            data-testid="grid-sidebar-top"
                            class="grid-sidebar-top"
                        >
                            <FilterButton button-style="text" />

                            <div
                                v-qa="'total-results'"
                                class="total-results"
                            >
                                {{ totalResultsCountLabel }}
                            </div>

                            <ThLink
                                v-qa="'filters-clear-all'"
                                :to="routeReset"
                                class="reset"
                            >
                                Reset
                            </ThLink>
                        </div>

                        <slot name="filters"></slot>
                    </div>

                    <div class="grid-content">
                        <div
                            ref="gridTopBarWrapperElement"
                            :class="classGridTopBarWrapper"
                            class="grid-top-bar-wrapper"
                        >
                            <div class="grid-top-bar">
                                <div class="grid-sidebar-top">
                                    <FilterButton />

                                    <div class="total-results">
                                        {{ totalResultsCountLabel }}
                                    </div>

                                    <ThLink
                                        v-qa="'filters-clear-all'"
                                        :to="routeReset"
                                        class="reset"
                                    >
                                        Reset
                                    </ThLink>
                                </div>

                                <div class="grid-toggle">
                                    <GridViewToggle
                                        v-model:table-view="tableView"
                                    />
                                </div>

                                <div class="grid-top-content">
                                    <SortingDropdown
                                        :model="sorting"
                                        :options="sortingOptions"
                                        :route-getter="getSortRoute"
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="grid-filter-chips">
                            <FilterChips
                                v-qa="'filter-chips-page'"
                                :chips="filterChips"
                                :route-reset
                            />
                        </div>

                        <div
                            v-if="stonesCollection?.length"
                            class="wrapper"
                        >
                            <StonePreviewTable
                                v-if="tableView"
                                :stones-collection
                                :sorting
                                :get-stone-route
                                class="table"
                                :class="classGrid"
                                @sort-by-carat="$emit('sort-by-carat')"
                                @sort-by-price="$emit('sort-by-price')"
                            />

                            <div
                                v-else
                                :class="classGrid"
                                class="cards"
                            >
                                <StonePreview
                                    v-for="stone in stonesCollection"
                                    :key="stone.id"
                                    class="card"
                                    :stone
                                    :stone-route="getStoneRoute(stone.id)"
                                    :sorting
                                    :data-qa-stone-props="
                                        JSON.stringify({
                                            id: stone.id,
                                        })
                                    "
                                />

                                <NoResultCta
                                    v-if="stonesCollection.length < 5"
                                    :category
                                    :small="!tableView"
                                />
                            </div>
                        </div>

                        <GridContentLoader v-else-if="fetchingOrStale" />

                        <div
                            v-else
                            class="no-results-found"
                        >
                            <ThSpacer size="LARGE" />

                            <slot name="no-results-found"></slot>

                            <NoResultCta :category />

                            <ThSpacer size="LARGE" />
                        </div>
                    </div>
                </div>
                <slot name="after-grid"></slot>
            </div>

            <div v-else>
                <StoneSearchFiveHundredError
                    :errors
                    :route-reset
                    @retry="retry"
                />
            </div>
        </div>

        <div v-else>
            <StoneSearchFiveHundredError
                :errors
                :route-reset
                @retry="retry"
            />
        </div>

        <FiltersModal
            v-if="isFilterModalRendered"
            :active-filter-count
            :data-qa="'filters-modal'"
            :fetching="fetchingOrStale"
            :is-open="isFilterModalOpen"
            :result-count="totalCount || 0"
            :route-reset
            @close="closeFilterModal"
        >
            <template #chips>
                <slot name="filters-chips"></slot>
            </template>

            <template #filters>
                <slot name="filters"></slot>
            </template>
        </FiltersModal>

        <slot name="stone-modal"></slot>
    </div>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent, hydrateOnVisible, toRef } from 'vue'
import { useI18n } from 'vue-i18n'

import {
    type GemstoneSearchQuery,
    type LabGrownSearchQuery,
    type NonSkuGemstoneBoundsFragment,
    type NonSkuGemstoneFragment,
    type NonSkuGemstoneSortInput,
    type NonSkuLabGrownDiamondBoundsFragment,
    type NonSkuLabGrownDiamondSortInput,
    type NonSkuLabGrownFragment,
    type StoneCategoryCode,
    type StoneShapeCode,
} from '@/graphql/api'

import type { UseQueryResponse } from '@urql/vue'
import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import type { ErrorCollectionWithTraces } from '@/helpers/errors'
import { useStickyGridFiltersBar } from '@/components/composables/useStickyGridFiltersBar'
import { useNumberFormatter } from '@/composables/useNumberFormatter'
import { useGridViewToggle } from '../useGridViewToggle'
import { gemsApiShapeToShapeCode } from '@/domain/stone/search/sku/shape.ts'
import { useFilterModal } from '@/composables/filter-modal.ts'

import FiltersModal from '@/components/ui/filter/FiltersModal.vue'
import SortingDropdown, {
    type DropdownOption,
} from '@/components/ui/SortingDropdown.vue'
import FilterButton from '@/components/product/filter-button/FilterButton.vue'
import ThSpacer from '@/components/ui/ThSpacer.vue'
import FilterChips, {
    type FilterChip,
} from '@/components/ui/filter/FilterChips.vue'
import GridViewToggle from '../GridViewToggle.vue'
import StoneSearchTabs from '@/components/stone/search/StoneSearchTabs.vue'
import ThLink from '@/components/ui/buttons/ThLink.vue'
import { useFilterButtonClient } from '@/components/product/filter-button/useFilterButton'

const GridContentLoader = defineAsyncComponent({
    loader: () => import('@/components/ui/GridContentLoader.vue'),
    hydrate: hydrateOnVisible(),
})

const NoResultCta = defineAsyncComponent({
    loader: () => import('../NoResultCta.vue'),
    hydrate: hydrateOnVisible(),
})

const StoneSearchFiveHundredError = defineAsyncComponent({
    loader: () => import('../StoneSearchFiveHundredError.vue'),
    hydrate: hydrateOnVisible(),
})

const StonePreviewTable = defineAsyncComponent({
    loader: () => import('../StonePreviewTable.vue'),
    hydrate: hydrateOnVisible(),
})

const StonePreview = defineAsyncComponent({
    loader: () => import('../../StonePreview.vue'),
    hydrate: hydrateOnVisible(),
})

const props = defineProps<{
    category: StoneCategoryCode
    bounds?:
        | NonSkuGemstoneBoundsFragment
        | NonSkuLabGrownDiamondBoundsFragment
        | null
    fetching: boolean
    stale: boolean
    response: UseQueryResponse<GemstoneSearchQuery | LabGrownSearchQuery>
    stonesCollection:
        | NonSkuGemstoneFragment[]
        | NonSkuLabGrownFragment[]
        | undefined
    shape?: StoneShapeCode
    sorting: NonSkuGemstoneSortInput | NonSkuLabGrownDiamondSortInput
    errors: ErrorCollectionWithTraces
    routeReset: ButtonRouteRelative
    filterChips: FilterChip[]
    sortingOptions:
        | DropdownOption<NonSkuGemstoneSortInput>[]
        | DropdownOption<NonSkuLabGrownDiamondSortInput>[]
    getSortRoute: (
        sort: NonSkuGemstoneSortInput | NonSkuLabGrownDiamondSortInput
    ) => ButtonRouteRelative
    activeFilterCount: number
    getStoneRoute: (id: string) => ButtonRouteRelative
}>()

defineEmits<{
    'sort-by-carat': []
    'sort-by-price': []
}>()

defineSlots<{
    'before-filters': () => any
    'filters-chips': () => any
    'filters': () => any
    'after-grid': () => any
    'last-page-filters': () => any
    'filters-quick': () => any
    'filters-quick-padded': () => any
    'stone-modal': () => any
    'no-results-found': () => any
}>()

const { t } = useI18n()

const executeQuery = toRef(() => props.response.executeQuery)
const data = toRef(() => props.response.data.value)
const error = toRef(() => props.response.error.value)

const totalCount = toRef(() => props.stonesCollection?.length)
const formatNumber = useNumberFormatter(0)

const totalResultsCountLabel = computed(() => {
    const count = totalCount.value || 0

    return t('catalogue.grid.results', count, {
        named: { count: formatNumber(count) },
    })
})

const {
    isFilterModalOpen,
    isFilterModalRendered,
    openFilterModal,
    closeFilterModal,
} = useFilterModal()

useFilterButtonClient(
    openFilterModal,
    computed(() => props.activeFilterCount)
)

const retry = () => {
    executeQuery.value({
        requestPolicy: 'network-only',
    })
}

const fetchingOrStale = computed(() => props.fetching || props.stale)

const { tableView, classGrid } = useGridViewToggle(fetchingOrStale)

const {
    gridTopBarWrapperElement,
    gridElement,
    classGridTopBarWrapper,
    gridTopBarHeight,
} = useStickyGridFiltersBar()
</script>

<style lang="scss" scoped>
@include stone-search;

.grid-sidebar-top,
.grid-top-bar-wrapper {
    height: v-bind(gridTopBarHeight);
}
</style>
